// apps-kb-move-mode.js - Icon move/drag mode for keyboard

import state from './apps-state.js';
import * as render from './apps-render.js';
import * as drag from './apps-drag.js';
import * as events from './apps-events.js';
import { keyboardState } from './apps-kb-state.js';
import * as helpers from './apps-kb-helpers.js';
import * as cursor from './apps-kb-cursor.js';
import * as scroll from './apps-kb-scroll.js';
import * as navLogic from './apps-kb-navigation-logic.js';

export function enterMoveMode() {
    if (!keyboardState.virtualCursor.active) return;
    
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    
    const icon = helpers.getIconAtRankFile(
        keyboardState.virtualCursor.rank,
        keyboardState.virtualCursor.file,
        icons,
        layout,
        isFolder
    );
    
    if (!icon) return;
    
    keyboardState.moveMode.active = true;
    keyboardState.moveMode.draggedIcon = icon;
    keyboardState.moveMode.originalRegion = icon.region;
    keyboardState.moveMode.originalPosition = icon.position;
    keyboardState.moveMode.originalFolder = icon.kfolder;
    
    const iconElement = document.querySelector(`.appicon[data-icon-id="${icon.id}"]`);
    if (!iconElement) return;
    
    state.dragGhost = events.createDragGhost(iconElement);
    
    state.setDraggingState(true);
    state.dragState = {
        icon: icon,
        originalRegion: icon.region,
        originalPosition: icon.position,
        originalFolder: icon.kfolder,
        isKeyboardDrag: true
    };
    
    const iconSlot = iconElement.closest('.icon-slot');
    if (iconSlot) {
        iconSlot.classList.add('drag-placeholder');
    }
    
    // Activate scroll zones to show hover hints (like in mouse drag)
    // On temporary screens, only show scroll zone to return to real screen
    const onLeftTemporary = state.temporaryScreenSide === 'left';
    const onRightTemporary = state.temporaryScreenSide === 'right';
    
    if (state.openFolder) {
        const leftZone = document.getElementById('folderScrollZoneLeft');
        const rightZone = document.getElementById('folderScrollZoneRight');
        
        // Only show right zone on left temporary (to go back right)
        if (onLeftTemporary) {
            if (rightZone) rightZone.classList.add('active');
        }
        // Only show left zone on right temporary (to go back left)
        else if (onRightTemporary) {
            if (leftZone) leftZone.classList.add('active');
        }
        // Show both zones on normal screens
        else {
            if (leftZone) leftZone.classList.add('active');
            if (rightZone) rightZone.classList.add('active');
        }
    } else {
        const leftZone = document.getElementById('scrollZoneLeft');
        const rightZone = document.getElementById('scrollZoneRight');
        
        // Only show right zone on left temporary (to go back right)
        if (onLeftTemporary) {
            if (rightZone) rightZone.classList.add('active');
        }
        // Only show left zone on right temporary (to go back left)
        else if (onRightTemporary) {
            if (leftZone) leftZone.classList.add('active');
        }
        // Show both zones on normal screens
        else {
            if (leftZone) leftZone.classList.add('active');
            if (rightZone) rightZone.classList.add('active');
        }
    }
}

export function exitMoveMode(cancelled = false) {
    if (!keyboardState.moveMode.active) return;
    
    // Deactivate scroll zones
    if (state.openFolder) {
        const leftZone = document.getElementById('folderScrollZoneLeft');
        const rightZone = document.getElementById('folderScrollZoneRight');
        if (leftZone) leftZone.classList.remove('active');
        if (rightZone) rightZone.classList.remove('active');
    } else {
        document.getElementById('scrollZoneLeft')?.classList.remove('active');
        document.getElementById('scrollZoneRight')?.classList.remove('active');
    }
    
    // Unhighlight modal zones if we're in one
    if (keyboardState.virtualCursor.isInModalZone) {
        const position = keyboardState.virtualCursor.modalZonePosition;
        if (position) {
            import('./apps-kb-modal.js').then(modal => {
                modal.unhighlightModalZone(position);
            });
        }
    }
    
    if (cancelled) {
        const icon = keyboardState.moveMode.draggedIcon;
        icon.region = keyboardState.moveMode.originalRegion;
        icon.position = keyboardState.moveMode.originalPosition;
        icon.kfolder = keyboardState.moveMode.originalFolder;
        
        if (state.dragGhost) {
            state.dragGhost.remove();
            state.dragGhost = null;
        }
        
        document.querySelectorAll('.drag-placeholder').forEach(el => {
            el.classList.remove('drag-placeholder');
        });
        
        drag.clearDropIndicators();
        
        state.dragState = null;
        state.setDraggingState(false);
    } else {
        // For keyboard drops, use the logical cursor position directly
        // No pixel round-trips - purely logical coordinate system
        const logicalRank = keyboardState.virtualCursor.rank;
        const logicalFile = keyboardState.virtualCursor.file;
        
        console.log('Keyboard drop at logical position:', { rank: logicalRank, file: logicalFile });
        
        // Set up intendedDropAction based on the logical position
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const currentScreen = helpers.getCurrentScreen();
        const icons = helpers.getCurrentIcons();
        const maxFile = layout.leaves * 4 - 1;
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        
        if (!Number.isInteger(logicalFile)) {
            // Fractional position (-0.5, X.5, MAX+0.5) - insert operation
            let targetRegion, targetPosition, insertBefore;
            
            if (logicalFile === -0.5) {
                const slotInfo = helpers.rankFileToRegionPosition(logicalRank, 0, layout, isFolder);
                targetRegion = screenStartRegion + slotInfo.region;
                targetPosition = slotInfo.position;
                insertBefore = true;
            } else if (logicalFile === maxFile + 0.5) {
                const slotInfo = helpers.rankFileToRegionPosition(logicalRank, maxFile, layout, isFolder);
                targetRegion = screenStartRegion + slotInfo.region;
                targetPosition = slotInfo.position;
                insertBefore = false;
            } else {
                // Insert between (e.g., 1.5 between 1 and 2)
                const rightFile = Math.ceil(logicalFile);
                const slotInfo = helpers.rankFileToRegionPosition(logicalRank, rightFile, layout, isFolder);
                targetRegion = screenStartRegion + slotInfo.region;
                targetPosition = slotInfo.position;
                insertBefore = true;
            }
            
            state.intendedDropAction = {
                type: insertBefore ? 'insert' : 'append',
                region: targetRegion,
                position: targetPosition,
                insertBefore: insertBefore,
                folder: state.openFolder ? state.openFolder.id : 0
            };
        } else {
            // Integer position - either move to empty slot or merge
            const slotInfo = helpers.rankFileToRegionPosition(logicalRank, logicalFile, layout, isFolder);
            const targetRegion = screenStartRegion + slotInfo.region;
            const targetPosition = slotInfo.position;
            
            // Check if slot is occupied
            const targetIcon = icons.find(icon => 
                icon.region === targetRegion && 
                icon.position === targetPosition
            );
            
            if (targetIcon && targetIcon.id !== keyboardState.moveMode.draggedIcon.id) {
                // Occupied - merge operation
                state.intendedDropAction = {
                    type: 'merge',
                    targetIconId: targetIcon.id,
                    folder: state.openFolder ? state.openFolder.id : 0
                };
            } else {
                // Empty slot - move operation
                state.intendedDropAction = {
                    type: 'move',
                    region: targetRegion,
                    position: targetPosition,
                    folder: state.openFolder ? state.openFolder.id : 0
                };
            }
        }
        
        // Call endDragging - it will use intendedDropAction
        // Pixel coordinates not needed for logical keyboard navigation
        drag.endDragging(0, 0);
        
        // For insert/append operations, re-select the icon after a brief delay
        // to ensure the icon is in its final position and not at a fractional file
        if (!Number.isInteger(logicalFile)) {
            const iconId = keyboardState.moveMode.draggedIcon.id;
            setTimeout(async () => {
                // Wait for DOM updates
                await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
                
                // Import cursor module
                const cursorModule = await import('./apps-kb-cursor.js');
                if (!cursorModule || typeof cursorModule.showVirtualCursor !== 'function') {
                    return;
                }
                
                // Find the icon by ID
                const icon = window.icons.find(ic => ic.id === iconId);
                if (!icon) {
                    return;
                }
                
                // Calculate visual position
                const layout = helpers.getLayout();
                const isFolder = icon.kfolder !== 0;
                
                let rank, file;
                if (isFolder && state.openFolder && state.openFolder.id === icon.kfolder) {
                    // Folder view: simpler calculation
                    const posRank = Math.floor(icon.position / 4);
                    const posFile = icon.position % 4;
                    const regionLeaf = icon.region;
                    rank = posRank;
                    file = regionLeaf * 4 + posFile;
                } else if (!isFolder) {
                    // Main screen: use calculateVisualPosition
                    const visualPos = render.calculateVisualPosition(icon.region, icon.position, layout);
                    rank = visualPos.rank;
                    file = visualPos.file;
                } else {
                    // Icon is in a folder that's not currently open - don't reselect
                    return;
                }
                
                // Re-select the icon at its new position
                cursorModule.showVirtualCursor(rank, file);
                console.log('Re-selected icon after keyboard insert/append:', { iconId, rank, file });
            }, 100); // Small delay to allow async operations to complete
        }
    }
    
    keyboardState.moveMode.active = false;
    keyboardState.moveMode.draggedIcon = null;
    keyboardState.moveMode.originalRegion = null;
    keyboardState.moveMode.originalPosition = null;
    keyboardState.moveMode.originalFolder = null;
}

/**
 * Convert file numbers from getAllValidHorizontalPositions into point objects with types
 * This determines whether each point is a scroll zone, empty slot, merge target, or insert point
 */
function buildPointObjects(validFiles, rank, icons, layout, isFolder, currentScreen) {
    const points = [];
    const maxFile = layout.leaves * 4 - 1;
    
    // Get icons in this rank (excluding dragged icon)
    const rankIcons = helpers.getIconsInRank(rank, icons, layout, isFolder);
    const draggedIcon = keyboardState.moveMode.draggedIcon;
    const nonDraggedIcons = rankIcons.filter(iconData => iconData.icon.id !== draggedIcon.id);
    
    // Create lookup map for quick icon finding by file
    const iconsByFile = new Map();
    nonDraggedIcons.forEach(iconData => {
        iconsByFile.set(iconData.file, iconData.icon);
    });
    
    for (const file of validFiles) {
        if (file === -1) {
            // Left scroll zone
            points.push({ type: 'scroll-left', file: -1 });
        } else if (file === maxFile + 1) {
            // Right scroll zone
            points.push({ type: 'scroll-right', file: maxFile + 1 });
        } else if (Number.isInteger(file)) {
            // Integer file - either occupied (merge) or empty slot
            const icon = iconsByFile.get(file);
            if (icon) {
                points.push({ type: 'merge', file, icon });
            } else {
                points.push({ type: 'empty-slot', file });
            }
        } else {
            // Fractional file - insert point
            // Determine which type based on the file value
            if (file === -0.5) {
                // Insert before file 0
                const refIcon = iconsByFile.get(0);
                points.push({ type: 'insert-before', file: -0.5, refIcon });
            } else if (file === maxFile + 0.5) {
                // Insert after maxFile
                const refIcon = iconsByFile.get(maxFile);
                points.push({ type: 'insert-after', file: maxFile + 0.5, refIcon });
            } else {
                // Insert between two adjacent files
                const leftFile = Math.floor(file);
                const rightFile = Math.ceil(file);
                const leftIcon = iconsByFile.get(leftFile);
                const rightIcon = iconsByFile.get(rightFile);
                points.push({
                    type: 'insert-between',
                    file,
                    leftIcon,
                    rightIcon,
                    leftFile,
                    rightFile
                });
            }
        }
    }
    
    // Sort points by file, then by type priority
    points.sort((a, b) => {
        if (a.file !== b.file) return a.file - b.file;
        
        const typeOrder = {
            'scroll-left': 0,
            'insert-before': 1,
            'merge': 2,
            'empty-slot': 3,
            'insert-between': 4,
            'insert-after': 5,
            'scroll-right': 6
        };
        return typeOrder[a.type] - typeOrder[b.type];
    });
    
    return points;
}

export function handleMoveModeHorizontalMovement(direction, layout, isFolder, icons) {
    const currentRank = keyboardState.virtualCursor.rank;
    let currentFile = keyboardState.virtualCursor.file;
    
    const maxFile = layout.leaves * 4 - 1;
    const draggedIcon = keyboardState.moveMode.draggedIcon;
    const currentScreen = helpers.getCurrentScreen();
    
    // Use centralized logic to get all valid horizontal positions
    const validFiles = navLogic.getAllValidHorizontalPositions(
        currentRank,
        draggedIcon,
        icons,
        layout,
        isFolder,
        currentScreen,
        maxFile
    );
    
    // Convert file numbers to point objects with types (merge, insert-between, etc.)
    const sortedPoints = buildPointObjects(validFiles, currentRank, icons, layout, isFolder, currentScreen);
    
    console.log('ÃƒÆ’Ã‚Â°Ãƒâ€¦Ã‚Â¸ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ ALL POINTS:', sortedPoints.map((p, i) => ({
        index: i,
        type: p.type,
        file: p.file
    })));
    
    // Find current point index in the sorted list
    let currentIndex = -1;
    
    if (keyboardState.scrollZoneHover === 'left') {
        // When on scroll-left and moving right, navigate to the first position after it
        if (direction === 'right') {
            scroll.hideScrollZoneHover();
            // Find the scroll-left position and move to the next position
            currentIndex = sortedPoints.findIndex(p => p.type === 'scroll-left');
            if (currentIndex !== -1) {
                // Move to next position
                currentIndex++;
                if (currentIndex >= sortedPoints.length) {
                    currentIndex = 0;
                }
                const newPos = sortedPoints[currentIndex];
                navigateToPoint(newPos, currentRank, layout, isFolder);
            }
            return;
        }
        // If going left, find scroll-left position and continue with normal navigation
        currentIndex = sortedPoints.findIndex(p => p.type === 'scroll-left');
    } else if (keyboardState.scrollZoneHover === 'right') {
        // When on scroll-right and moving left, navigate to the first position before it
        if (direction === 'left') {
            scroll.hideScrollZoneHover();
            // Find the scroll-right position and move to the previous position
            currentIndex = sortedPoints.findIndex(p => p.type === 'scroll-right');
            if (currentIndex !== -1) {
                // Move to previous position
                currentIndex--;
                if (currentIndex < 0) {
                    currentIndex = sortedPoints.length - 1;
                }
                const newPos = sortedPoints[currentIndex];
                navigateToPoint(newPos, currentRank, layout, isFolder);
            }
            return;
        }
        // If going right, find scroll-right position and continue with normal navigation
        currentIndex = sortedPoints.findIndex(p => p.type === 'scroll-right');
    } else if (keyboardState.virtualCursor.isInInsertZone) {
        const insertType = keyboardState.virtualCursor.insertZoneType;
        
        for (let i = 0; i < sortedPoints.length; i++) {
            const pos = sortedPoints[i];
            
            if (insertType === 'between' && pos.type === 'insert-between') {
                // For insert-between, the cursor file IS already the fractional file (e.g., 2.5)
                // Just match it directly
                if (Math.abs(pos.file - currentFile) < 0.1) {
                    currentIndex = i;
                    break;
                }
            } else if ((insertType === 'before' && pos.type === 'insert-before') ||
                       (insertType === 'after' && pos.type === 'insert-after')) {
                if (pos.file === currentFile) {
                    currentIndex = i;
                    break;
                }
            }
        }
    } else {
        for (let i = 0; i < sortedPoints.length; i++) {
            if (sortedPoints[i].file === currentFile) {
                if (sortedPoints[i].type === 'merge' || sortedPoints[i].type === 'empty-slot') {
                    currentIndex = i;
                    break;
                } else if (currentIndex === -1) {
                    currentIndex = i;
                }
            }
        }
    }
    
    if (currentIndex === -1) {
        let minDist = Infinity;
        for (let i = 0; i < sortedPoints.length; i++) {
            const dist = Math.abs(sortedPoints[i].file - currentFile);
            if (dist < minDist) {
                minDist = dist;
                currentIndex = i;
            }
        }
    }
    
    if (direction === 'left') {
        currentIndex--;
    } else {
        currentIndex++;
    }
    
    if (currentIndex < 0) {
        currentIndex = sortedPoints.length - 1;
    } else if (currentIndex >= sortedPoints.length) {
        currentIndex = 0;
    }
    
    const newPos = sortedPoints[currentIndex];
    
    console.log('ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â°ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¸ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚ÂÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â NAVIGATION:', {
        direction,
        fromFile: currentFile,
        fromInsertZone: keyboardState.virtualCursor.isInInsertZone,
        fromInsertType: keyboardState.virtualCursor.insertZoneType,
        toType: newPos.type,
        toFile: newPos.file,
        totalPoints: sortedPoints.length,
        currentIndex
    });
    
    navigateToPoint(newPos, currentRank, layout, isFolder);
}

function navigateToPoint(pos, rank, layout, isFolder) {
    console.log('ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â°ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¸ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â NAVIGATE TO:', {
        posType: pos.type,
        posFile: pos.fil,
        rank: rank
    });
    
    const currentScreen = helpers.getCurrentScreen();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenFirstVisualRank = isFolder ? 0 : Math.floor((currentScreen * regionsPerScreen) / layout.leaves) * 3;
    const absoluteVisualRank = screenFirstVisualRank + rank;
    
    // Get correct container for queries
    const container = isFolder ? 
        document.getElementById('folderScreens') : 
        document.getElementById('appspaceScreens');
    
    if (!container) return;
    
    keyboardState.virtualCursor.isInInsertZone = false;
    keyboardState.virtualCursor.insertZoneType = null;
    
    switch (pos.type) {
        case 'scroll-left':
        case 'scroll-right':
            // Save current cursor position before moving to scroll zone
            if (!keyboardState.scrollZoneHover) {
                keyboardState.scrollZonePreviousPosition = {
                    rank: keyboardState.virtualCursor.rank,
                    file: keyboardState.virtualCursor.file,
                    isInInsertZone: keyboardState.virtualCursor.isInInsertZone,
                    insertZoneType: keyboardState.virtualCursor.insertZoneType
                };
            }
            drag.clearDropIndicators();
            scroll.showScrollZoneHover(pos.type === 'scroll-left' ? 'left' : 'right');
            // Show cursor at scroll zone position (triggers special scroll zone rendering)
            cursor.showVirtualCursor(rank, pos.file);
            break;
            
        case 'insert-before':
            scroll.hideScrollZoneHover();
            // Always use the new cursor system which properly centers within gaps
            cursor.showVirtualCursor(rank, pos.file);
            
            // For drop target positioning, get the actual pixel position
            if (pos.refIcon) {
                const iconElement = container.querySelector(`[data-icon-id="${pos.refIcon.id}"]`);
                const slotElement = iconElement?.closest('.icon-slot');
                if (slotElement) {
                    const slotRect = slotElement.getBoundingClientRect();
                    const gapH = parseInt(
                        getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')
                    ) || state.config.gapHorizontal || 16;
                    const clientX = slotRect.left - (gapH / 2);
                    const clientY = slotRect.top + slotRect.height / 2;
                    
                    drag.updateDropTarget(clientX, clientY);
                }
            } else {
                updateKeyboardDragIndicators(rank, pos.file);
            }
            // Set these AFTER showVirtualCursor (which calls hideVirtualCursor)
            keyboardState.virtualCursor.isInInsertZone = true;
            keyboardState.virtualCursor.insertZoneType = 'before';
            break;
            
        case 'insert-after':
            scroll.hideScrollZoneHover();
            // Always use the new cursor system which properly centers within gaps
            cursor.showVirtualCursor(rank, pos.file);
            
            // For drop target positioning, get the actual pixel position
            if (pos.refIcon) {
                const iconElement = container.querySelector(`[data-icon-id="${pos.refIcon.id}"]`);
                const slotElement = iconElement?.closest('.icon-slot');
                if (slotElement) {
                    const slotRect = slotElement.getBoundingClientRect();
                    const gapH = parseInt(
                        getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')
                    ) || state.config.gapHorizontal || 16;
                    const clientX = slotRect.right + (gapH / 2);
                    const clientY = slotRect.top + slotRect.height / 2;
                    
                    drag.updateDropTarget(clientX, clientY);
                }
            } else {
                updateKeyboardDragIndicators(rank, pos.file);
            }
            // Set these AFTER showVirtualCursor (which calls hideVirtualCursor)
            keyboardState.virtualCursor.isInInsertZone = true;
            keyboardState.virtualCursor.insertZoneType = 'after';
            break;
            
        case 'insert-between':
            scroll.hideScrollZoneHover();
            // Always use the new cursor system
            cursor.showVirtualCursor(rank, pos.file);
            
            // For drop target positioning, calculate center between icons
            if (pos.leftIcon && pos.rightIcon) {
                const leftElement = container.querySelector(`[data-icon-id="${pos.leftIcon.id}"]`);
                const rightElement = container.querySelector(`[data-icon-id="${pos.rightIcon.id}"]`);
                const leftSlot = leftElement?.closest('.icon-slot');
                const rightSlot = rightElement?.closest('.icon-slot');
                
                if (leftSlot && rightSlot) {
                    const leftRect = leftSlot.getBoundingClientRect();
                    const rightRect = rightSlot.getBoundingClientRect();
                    const clientX = (leftRect.right + rightRect.left) / 2;
                    const clientY = leftRect.top + leftRect.height / 2;
                    
                    drag.updateDropTarget(clientX, clientY);
                }
            } else {
                updateKeyboardDragIndicators(rank, pos.file);
            }
            // Set these AFTER showVirtualCursor (which calls hideVirtualCursor)
            keyboardState.virtualCursor.isInInsertZone = true;
            keyboardState.virtualCursor.insertZoneType = 'between';
            break;
            
        case 'merge':
            scroll.hideScrollZoneHover();
            // Always use the new cursor system
            cursor.showVirtualCursor(rank, pos.file);
            
            // For drop target positioning
            if (pos.icon) {
                const iconElement = container.querySelector(`[data-icon-id="${pos.icon.id}"]`);
                if (iconElement) {
                    const rect = iconElement.getBoundingClientRect();
                    const clientX = rect.left + rect.width / 2;
                    const clientY = rect.top + rect.height / 2;
                    drag.updateDropTarget(clientX, clientY);
                }
            }
            break;
            
        case 'empty-slot':
            scroll.hideScrollZoneHover();
            cursor.showVirtualCursor(rank, pos.file);
            updateKeyboardDragIndicators(rank, pos.file);
            break;
    }
    
    console.log('ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã¢â‚¬Å“ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¦ AFTER NAVIGATION:', {
        cursorRank: keyboardState.virtualCursor.rank,
        cursorFile: keyboardState.virtualCursor.file,
        isInInsertZone: keyboardState.virtualCursor.isInInsertZone,
        insertZoneType: keyboardState.virtualCursor.insertZoneType,
        scrollZoneHover: keyboardState.scrollZoneHover
    });
}

export function updateKeyboardDragIndicators(rank, file) {
    if (!keyboardState.moveMode.active) return;
    
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const currentScreen = helpers.getCurrentScreenForRegions();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    // Handle insert positions (fractional files) specially
    if (navLogic.isInsertPosition(file)) {
        updateInsertPositionIndicators(rank, file, layout, isFolder, currentScreen, screenStartRegion, regionsPerScreen);
        return;
    }
    
    // Handle regular icon slots
    const slotInfo = helpers.rankFileToRegionPosition(rank, file, layout, isFolder);
    if (!slotInfo) return;
    
    const actualRegion = screenStartRegion + slotInfo.region;
    
    // Scope query to correct container
    const container = isFolder ? 
        document.getElementById('folderScreens') : 
        document.getElementById('appspaceScreens');
    
    if (!container) return;
    
    // CRITICAL: Query within the specific screen element, not the entire container
    // This prevents finding slots from temporary screens with duplicate region numbers
    const visualScreen = isFolder ? state.currentFolderScreen : state.currentScreen;
    const screenElement = container.children[visualScreen];
    
    if (!screenElement) {
        console.error('Screen element not found:', visualScreen);
        return;
    }
    
    const slotElement = screenElement.querySelector(
        `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`
    );
    
    if (!slotElement) return;
    
    const slotRect = slotElement.getBoundingClientRect();
    const clientX = slotRect.left + slotRect.width / 2;
    const clientY = slotRect.top + slotRect.height / 2;
    
    if (state.dragGhost) {
        const computedIconSize = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
        ) || state.config.iconSize;
        
        state.dragGhost.style.left = (clientX - computedIconSize / 2) + 'px';
        state.dragGhost.style.top = (clientY - computedIconSize / 2) + 'px';
        
        // Also update dragState coordinates for drop operation
        if (state.dragState) {
            state.dragState.currentX = clientX;
            state.dragState.currentY = clientY;
        }
    }
    
    drag.updateDropTarget(clientX, clientY);
}

function updateInsertPositionIndicators(rank, file, layout, isFolder, currentScreen, screenStartRegion, regionsPerScreen) {
    const maxFile = navLogic.getMaxFile(layout);
    const container = isFolder ? 
        document.getElementById('folderScreens') : 
        document.getElementById('appspaceScreens');
    
    if (!container) return;
    
    const visualScreen = isFolder ? state.currentFolderScreen : state.currentScreen;
    const screenElement = container.children[visualScreen];
    
    if (!screenElement) return;
    
    const gapH = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')
    ) || state.config.gapHorizontal || 16;
    
    let clientX, clientY;
    
    if (file === -0.5) {
        // Insert before first icon (file 0)
        const slotInfo = helpers.rankFileToRegionPosition(rank, 0, layout, isFolder);
        if (!slotInfo) return;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        const slotElement = screenElement.querySelector(
            `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`
        );
        
        if (!slotElement) return;
        const slotRect = slotElement.getBoundingClientRect();
        clientX = slotRect.left - (gapH / 2);
        clientY = slotRect.top + slotRect.height / 2;
        
    } else if (file === maxFile + 0.5) {
        // Insert after last icon (file MAX)
        const slotInfo = helpers.rankFileToRegionPosition(rank, maxFile, layout, isFolder);
        if (!slotInfo) return;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        const slotElement = screenElement.querySelector(
            `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`
        );
        
        if (!slotElement) return;
        const slotRect = slotElement.getBoundingClientRect();
        clientX = slotRect.right + (gapH / 2);
        clientY = slotRect.top + slotRect.height / 2;
        
    } else {
        // Insert between two icons
        const leftFile = Math.floor(file);
        const rightFile = Math.ceil(file);
        
        const leftSlotInfo = helpers.rankFileToRegionPosition(rank, leftFile, layout, isFolder);
        const rightSlotInfo = helpers.rankFileToRegionPosition(rank, rightFile, layout, isFolder);
        
        if (!leftSlotInfo || !rightSlotInfo) return;
        
        const leftActualRegion = screenStartRegion + leftSlotInfo.region;
        const rightActualRegion = screenStartRegion + rightSlotInfo.region;
        
        const leftSlotElement = screenElement.querySelector(
            `.icon-slot[data-region="${leftActualRegion}"][data-position="${leftSlotInfo.position}"]`
        );
        const rightSlotElement = screenElement.querySelector(
            `.icon-slot[data-region="${rightActualRegion}"][data-position="${rightSlotInfo.position}"]`
        );
        
        if (!leftSlotElement || !rightSlotElement) return;
        
        const leftRect = leftSlotElement.getBoundingClientRect();
        const rightRect = rightSlotElement.getBoundingClientRect();
        clientX = (leftRect.right + rightRect.left) / 2;
        clientY = leftRect.top + leftRect.height / 2;
    }
    
    // Update drop target with the insert position
    drag.updateDropTarget(clientX, clientY);
}
